<template>
  <div>
    <v-card
      :class="'auth-ui-card--has-tabs'"
    >
      <div slot="header-title">
        {{ $t('admin.magicLinkMailingListsTitle') }}
      </div>
      <div slot="body">
        <div>
          <div class="auth-ui-card-feature-description">
            <p>
              {{ $t('admin.magicLinkMailingListsDescription') }}
            </p>
          </div>

          <!-- Template selection -->
          <v-template-section
            :selected-template="selectedTemplate"
            :toggle-modal="toggleTemplateModal"
          />

          <!-- Mailing list selection -->
          <div class="field">
            <label class="label auth-ui-select-label">
              <span class="auth-ui-select-wide-screen-label">
                <div class="auth-ui-label-icon-container">
                  {{ $t('admin.magicLinkSelectMailingListTitle') }}
                  <i
                    :title="$t('admin.magicLinkMailingListsTooltip')"
                    class="fa fa-refresh auth-ui-mailing-list-refresh
                     auth-ui-mailing-list-refresh--is-spinning"
                    :disabled:="iconIsDisabled"
                    @click.prevent="getMailingLists()"
                  />
                </div>
                <div>
                  {{ $t('admin.magicLinkSelectContactsLabel') }}
                </div>
                <div class="auth-ui-double-card-label--has-margin-right">
                  {{ $t('admin.magicLinkSelectReportsLabel') }}
                </div>
              </span>
              <span class="auth-ui-select-narrow-screen-label">
                <div class="auth-ui-label-icon-container">
                  {{ $t('admin.magicLinkSelectMailingListTitleNarrow') }}
                  <i
                    :title="$t('admin.magicLinkMailingListsTooltip')"
                    class="fa fa-refresh auth-ui-mailing-list-refresh
                     auth-ui-mailing-list-refresh--is-spinning"
                    :disabled:="iconIsDisabled"
                    @click.prevent="getMailingLists()"
                  />
                </div>
              </span>
            </label>
            <div class="control">
              <div class="select is-multiple auth-ui-card-select">
                <div
                  class="auth-ui-select-box"
                >
                  <div
                    v-for="mailingList in mailingLists"
                    :id="mailingList.name"
                    :key="mailingList.id"
                    class="auth-ui-select-option"
                    @click="selectMailinglist(mailingList, true)"
                  >
                    <div class="auth-ui-row-container auth-ui-select-option-container">
                      <div class="auth-ui-select-option-narrow-header">
                        {{ $t('admin.magicLinkSelectListSubTitleNarrow') }}
                      </div>
                      <div
                        :title="mailingList.name"
                        class="auth-ui-select-text-row"
                      >
                        {{ mailingList.name }}
                      </div>
                    </div>

                    <div class="auth-ui-row-container auth-ui-select-option-container">
                      <div class="auth-ui-select-option-narrow-header">
                        {{ $t('admin.magicLinkSelectContactsSubTitleNarrow') }}
                      </div>
                      <div>
                        {{ mailingList.contact_count }}
                      </div>
                    </div>
                    <div class="auth-ui-row-container auth-ui-select-option-container">
                      <div class="auth-ui-select-option-narrow-header">
                        {{ $t('admin.magicLinkSelectReportsSubTitleNarrow') }}
                      </div>
                      <div class="auth-ui-select-report-container">
                        <span>
                          <i
                            v-if="mailingList.reports && mailingList.reports.length"
                            class="fa fa-file-text-o"
                            aria-hidden="true"
                            @click.prevent="toggleReportModal(mailingList)"
                          />
                          ({{ mailingList.reports ? mailingList.reports.length : 0 }})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="auth-ui-select-bottom-description">
              {{ $t('admin.magicLinkMailingListsBottomDescription') }}
              <span class="auth-ui-select-chosen-option-text">{{ defaultMailingList }}</span>.
            </div>
          </div>
        </div>
        <div class="field">
          <div class="control auth-ui-row-container">
            <div
              v-if="hasError"
              class="auth-ui-mail-send-message help is-danger"
            >
              {{ error }}
            </div>
            <div
              v-else-if="hasSuccess && !hasAlarm"
              class="auth-ui-mail-send-message help is-success"
            >
              {{ success }}
            </div>
            <div
              v-else-if="hasSuccess"
              class="auth-ui-mail-send-message help is-warning"
            >
              {{ success }}
            </div>
            <half-circle-spinner
              v-if="showSpinner"
              :animation-duration="1000"
              :size="25"
              class="auth-ui-spinner-wrapper auth-ui-double-card-spinner"
            />
            <button
              :disabled="sendButtonIsDisabled"
              class="button is-link auth-ui-pulled-right has-negative-margin"
              @click.prevent="sendMagicLinksToMailingList()"
            >
              {{ $t('admin.magicLinkMailingListsButtonText') }}
            </button>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Modals here -->
    <v-mailing-list-send-report-modal ref="reportModal" />
    <v-template-selection-modal
      ref="templateModal"
      :template-refresh-ready="templateRefreshReady"
      :refresh-templates="refreshTemplates"
      :select-template="selectTemplate"
      :templates="dynamicTemplates"
    />
  </div>
</template>

<script>
/* ============
   * Send One Time Links Mailing List Component
   * ============
   *
   * One of the tabs of the Mail Sends component.
   * Used for sending magic links to a mailing list.
   *
   */
import { HalfCircleSpinner } from 'epic-spinners';
import VCard from '@/components/Card.vue';
import Request from '@/tools/Request';
import VMailingListSendReportModal from './CustomComponents/MailingListSendReportModal.vue';
import VTemplateSelectionModal from './CustomComponents/TemplateSelectionModal.vue';
import VTemplateSection from './CustomComponents/TemplateSection.vue';

export default {
  name: 'SendOneTimeLinksMailingList',

  components: {
    VCard,
    VMailingListSendReportModal,
    VTemplateSelectionModal,
    VTemplateSection,
    HalfCircleSpinner,
  },

  props: {
    dynamicTemplates: {
      type: Array,
      default: () => [],
    },
    refreshTemplates: {
      type: Function,
      default: () => {},
    },
    templateRefreshReady: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      hasError: false,
      error: '',
      hasSuccess: false,
      success: '',
      hasAlarm: false,
      iconIsDisabled: false,
      isDisabled: false,
      showSpinner: false,
      mailingLists: [],
      selectedMailingList: {
        name: '',
        contact_count: 0,
        id: '',
      },
      selectedTemplate: this.dynamicTemplates.length ? this.dynamicTemplates[0] : {},
    };
  },

  computed: {
    defaultMailingList() {
      return this.selectedMailingList.name.length ? this.selectedMailingList.name : this.$t('admin.magicLinkNoListSelected');
    },

    sendButtonIsDisabled() {
      return !this.selectedMailingList.name.length;
    },
  },

  watch: {
    templateRefreshReady(newVal) {
      if (newVal) {
        if (this.dynamicTemplates.length) {
          this.selectedTemplate = this.dynamicTemplates[0];
        } else {
          this.selectedTemplate = {};
        }
      }
    },
  },

  created() {
    this.getMailingLists();
  },

  updated() {
    if (this.selectedMailingList.name.length) {
      this.selectMailinglist(this.selectedMailingList);
    }
  },

  methods: {
    enable() {
      this.isDisabled = false;
    },

    disable() {
      this.isDisabled = true;
    },

    enableIcon() {
      this.iconIsDisabled = false;
    },

    disableIcon() {
      this.iconIsDisabled = true;
    },

    setAlarm(value) {
      this.hasAlarm = value;
    },

    /**
     * Chooses a mailing list for sendMagicLinksToMailingList().
     */
    selectMailinglist(mailingList, clearStatus = false) {
      if (clearStatus) {
        this.error = '';
        this.success = '';
        this.hasError = false;
        this.hasSuccess = false;
        this.setAlarm(false);
      }

      if (this.selectedMailingList.name.length
        && document.getElementById(this.selectedMailingList.name)) {
        document.getElementById(this.selectedMailingList.name).classList.remove('auth-ui-select-option--is-selected');
      }
      this.selectedMailingList = mailingList;
      if (document.getElementById(this.selectedMailingList.name)) {
        document.getElementById(this.selectedMailingList.name).classList.add('auth-ui-select-option--is-selected');
      }
    },

    /**
     * Retrieves mailing lists in batches using recursion.
     */
    async getMailingLists(pageToken = '') {
      if (this.iconIsDisabled && !pageToken.length) {
        return;
      }

      this.disableIcon();
      this.hasError = false;
      this.toggleGetMailingListsSpinner(true);
      let url = `${process.env.VUE_APP_BACKEND_URL}/get_mailing_lists`;

      if (pageToken.length) {
        url = `${url}?pageToken=${pageToken}`;
      } else {
        this.mailingLists = [];
      }

      try {
        const response = await Request.submit('GET', url);
        this.toggleGetMailingListsSpinner(false);
        this.mailingLists = this.mailingLists.concat(response.mailingLists);

        if (response.status && response.status === 'ReportError') {
          this.hasError = true;
          this.error = this.$t('admin.magicLinkMailingListReportError');
        }

        if (response.pageToken) {
          await this.getMailingLists(response.pageToken);
        } else {
          this.enableIcon();
          this.toggleGetMailingListsSpinner(false);
        }
      } catch (error) {
        this.enableIcon();
        this.toggleGetMailingListsSpinner(false);
        this.mailingLists = [];
        this.hasError = true;
        this.error = this.$t('admin.magicLinkMailingListRetrievalError');
      }
    },

    /**
     * Adds/removes the spinning animation from the mailing list selection's refresh icon.
     */
    toggleGetMailingListsSpinner(show) {
      const iconElement = document.querySelector('.auth-ui-mailing-list-refresh');
      const narrowIconElement = document.querySelectorAll('.auth-ui-mailing-list-refresh')[1];
      const spinnerClass = 'auth-ui-mailing-list-refresh--is-spinning';

      if (!iconElement) return;

      if (show && !iconElement.classList.contains(spinnerClass)
        && !narrowIconElement.classList.contains(spinnerClass)) {
        iconElement.classList.add(spinnerClass);
        narrowIconElement.classList.add(spinnerClass);
      } else {
        iconElement.classList.remove(spinnerClass);
        narrowIconElement.classList.remove(spinnerClass);
      }
    },

    /**
     * Adds all contacts in the selected mailing list to a magic link send queue/index.
     */ // eslint-disable-next-line consistent-return
    async sendMagicLinksToMailingList() {
      if (this.isDisabled) {
        return;
      }

      this.disable();

      if (this.selectedMailingListHasError()) {
        this.enable();
        return;
      }

      this.showSpinner = true;

      try {
        const url = `${process.env.VUE_APP_BACKEND_URL}/send_magic_links_to_mailing_list`;
        const response = await Request.submit('POST', url, {
          mailingList: this.selectedMailingList,
          template: this.selectedTemplate,
        });

        if (response.status && response.status === 'RequestAccepted') {
          this.success = this.$t('admin.magicLinkMailingListLargeSendSuccess');
        } else if (response.sendStatus && response.sendStatus.status === 'PartialSuccess') {
          this.success = this.$t('admin.magicLinkMailingListSendPartialSuccess');
          this.setAlarm(true);
          this.updateMailingList(response);
        } else if (response.sendStatus && response.sendStatus.status === 'Success') {
          this.success = this.$t('admin.magicLinkMailingListSendSuccess');
          this.setAlarm(false);
          this.updateMailingList(response);
        }

        this.showSpinner = false;
        this.hasSuccess = true;
        this.enable();
      } catch (error) {
        this.showSpinner = false;
        this.hasSuccess = false;
        this.setAlarm(false);
        this.error = this.$t('admin.magicLinkGenericSendError');
        this.hasError = true;
        this.enable();
      }
    },

    /**
     * Updates the given mailing list with the response from the server.
     */
    updateMailingList(response) {
      this.toggleGetMailingListsSpinner(true);

      const updatedMailingList = response.sendStatus.mailingList;
      const sendStatus = response.sendStatus;

      for (let i = 0; i < this.mailingLists.length; i += 1) {
        if (this.mailingLists[i].id === updatedMailingList.id) {
          const newReport = {
            batchSize: sendStatus.batchSize,
            contactsCount: this.mailingLists[i].contact_count,
            errorData: sendStatus.errorData,
            status: sendStatus.status,
            time: sendStatus.time,
          };

          if (!this.mailingLists[i].reports) {
            this.mailingLists[i].reports = [];
          }

          this.mailingLists[i].reports.unshift(newReport);

          if (this.mailingLists[i].reports.length > 3) {
            this.mailingLists[i].reports.pop();
          }
        }
      }
      if (!response.reportSuccess) {
        this.setAlarm(true);
        this.success += this.$t('admin.magicLinkMailingListReportFailure');
      }

      this.toggleGetMailingListsSpinner(false);
    },

    /**
     * Returns true if the selected mailing list is empty or if no list is selected.
     */
    selectedMailingListHasError() {
      if (!this.selectedMailingList.id.length) {
        this.error = this.$t('admin.magicLinkNoListSelectedError');
        this.hasError = true;
        return true;
      }

      if (this.selectedMailingList.contact_count < 1) {
        this.error = this.$t('admin.magicLinkSelectedListEmptyError');
        this.hasError = true;
        return true;
      }

      this.hasError = false; // clear older errors
      return false;
    },

    toggleReportModal(mailingList) {
      this.$refs.reportModal.toggleModal(mailingList);
    },

    toggleTemplateModal() {
      this.$refs.templateModal.toggleModal(this.dynamicTemplates);
    },

    selectTemplate(template) {
      this.selectedTemplate = template;
    },
  },
};
</script>
